import { Injectable } from '@angular/core';
import { StorageService } from './storage.service';
import { ICartItem, IExtra, IExtraCartItem, ISearchParams, IShopCart } from '../interfaces';
import { BehaviorSubject } from 'rxjs';
import { AvailabilityService } from './availability.service';
import dayjs from 'dayjs';
import forEach from 'lodash/forEach';
import { priceExtraForRoom } from '../extra-room-price';

@Injectable({
  providedIn: 'root'
})
export class ShopcartService {

  /*
   * Service for adding and retriveing shop cart items
   * Cart items are saved in session storage and at the same time updated in the backend. 
   * They should be removed if availability page is shown again or a new search is made
   */

  shopCart$ = new BehaviorSubject<IShopCart|null>(null);
  // expose the BehaviorSubject as an Observable
  shopCart = this.shopCart$.asObservable();

  constructor(private storage:StorageService) {
    this.loadShopCart();
  }

  addCartItem(item:any, searchParams:ISearchParams) {
    let ci:ICartItem = new CartItem('roomrate', item, searchParams, [], {});
    let cart = this.shopCart$.getValue();
    if (cart == null) {
      cart = this.initShopCart();
      cart?.items.push(ci);
    }
    else {
      cart?.items.push(ci);
    }
    this.shopCart$.next(cart);
    this.saveShopCart();
  }

  replaceCartItem(index:number, item:any, searchParams:ISearchParams) {
    let cart = this.shopCart$.getValue();
    let newCartItem:ICartItem = new CartItem('roomrate', item, searchParams, [], {});
    if (cart) {
      let items = cart.getCartItemsByType('roomrate');
      items[index] = newCartItem;
      cart.items = items;
      this.shopCart$.next(cart);
      this.saveShopCart();
    }
    
  }

  initShopCart() {
    //creates an empty shopcart with current search params.
    let cart:IShopCart = new ShopCart([]);
    return cart;
  }

  removeCartItem() {}

  clearShopCart() {
    this.shopCart$.next(null);
    this.storage.removeSessionStorage('shopcart');
  }

  saveShopCart() {
    this.storage.saveSessionStorage('shopcart', this.shopCart$.getValue());
  }
  loadShopCart() {
    let cartFromStorage = this.storage.loadSessionStorage('shopcart');
    // create a class of the  loaded cart because json loses the methods
    if (cartFromStorage) {
      let cartItems:ICartItem[] = [];
      cartFromStorage.items.forEach((ci:ICartItem) => {
        cartItems.push(new CartItem(ci.type, ci.item, ci.searchParams ,ci.extras, ci.profile));
      });
      let cart = new ShopCart(cartItems);
      this.shopCart$.next(cart); 
    }
    
  }
  getCart() {
    return this.shopCart$.getValue();
  }

  getCartHotelId() {
    return this.getCart()?.getFirstCartItem().searchParams.hotelCode;
  }

  addExtra(extra:IExtraCartItem, roomIndex:number) {
    let cart = this.shopCart$.getValue();
    if (cart == null) {
      console.error('ADD EXTRA: cart is null');
    }
    else {
      // the cart shows and the reservation sends the room's own price and signature
      cart.items[roomIndex].extras.push(priceExtraForRoom(extra, roomIndex));
      this.shopCart$.next(cart);
      this.saveShopCart();
    }
  }

  editExtrasForAllRooms(extras:IExtraCartItem[]):void {
    let cart = this.shopCart$.getValue();
    if (cart == null) {
      console.error('EDIT EXTRA: cart is null');
    }
    else {
      let extraId = extras[0].rateId;
      cart.items.forEach((cartItem:ICartItem, index: number) => {
        
        let foundExtraIndex = cartItem.extras.findIndex((e) => e.rateId == extraId);
        if (foundExtraIndex != -1) {
          if (extras[index].quantity == 0) {
            cartItem.extras.splice(foundExtraIndex,1);
          }
          else {
            cartItem.extras.splice(foundExtraIndex,1,priceExtraForRoom(extras[index], index));
          }
        }
        else {
          if (extras[index].quantity > 0) {
            cartItem.extras.push(priceExtraForRoom(extras[index], index));
          }
        }
      });

      this.shopCart$.next(cart);
      this.saveShopCart();
    }
  }

  addProfiles(profiles:any) {
    let cart = this.shopCart$.getValue();
    if (cart == null) {
      console.error('ADD PROFILES: cart is null');
    }
    else {
      cart.items.forEach((cartItem:ICartItem, index: number) => {
        cartItem.profile = profiles[index];
      });

      this.shopCart$.next(cart);
      this.saveShopCart();
    }
  }   
  
}

export class ShopCart {
  items;
  
  constructor(items:ICartItem[]) {
    this.items = items;
  };

  getFirstCartItem() {
    return this.items[0] ?? null;
  };
  
  getFirstSearchParams() {
    return this.items[0].searchParams;
  };
  getFirstRoomPim() {
    return this.items[0]?.item?.room?.pim ?? null;
  };
  getFirstRatePim() {
    return this.items[0]?.item?.rate?.pim ?? null;
  };
  getCartItemsByType(type:'roomrate'|'extra'):ICartItem[] {
    let matches = this.items.filter((item:ICartItem) => {
      return item.type == type;
    })
    return matches;
  }
  getTotalPrice():number {
    let total:number = 0;
    this.items.forEach((cartItem:ICartItem) => {
      if (cartItem.type == 'roomrate') {
        total += cartItem.item.totalPriceForStayWithTaxes;
      }
      cartItem.extras.forEach((extra:IExtraCartItem) => {
        total += extra.totalPriceForStayWithTaxes * extra.quantity
      });
    });
    return total;
  }

  getDaysDelta():number {
    let searchParams = this.getFirstSearchParams()
    if (searchParams) {
        return dayjs(searchParams.departure).diff(searchParams.arrival, 'day');
    }
    return 0;
  }

  getExtra(extraId:string) {
    //returns an extra and the roomindex that has it
    let res:any[] = [];
    this.items.forEach((cartItem:ICartItem, index) => {
      let found = cartItem.extras.find((item:IExtraCartItem) => {return item.rateId == extraId});
      if (found) {
        res.push({
          roomIndex: index,
          item: found
        });
      }
    });
    return res;
  }

  hasExternalExtras():boolean {
    let hasExternalExtras = false;
    this.items.forEach((cartItem:ICartItem, index) => {
      let found = cartItem.extras.find((item:IExtraCartItem) => {return item.externalId});
      if (found) {
        hasExternalExtras = true;
      }
    });
    return hasExternalExtras;
  }
};

export class CartItem {
  type: string;
  item: any;
  searchParams: ISearchParams;
  extras: IExtraCartItem[];
  profile: any;

  constructor(type:string, item:any, searchParams:ISearchParams, extras:IExtraCartItem[], profile:any) {
    this.type = type;
    this.item = item;
    this.searchParams = searchParams;
    this.extras = extras ?? [];
    this.profile = profile ?? {};
  }

  getExtraById(extraId:string) {
    return this.extras.find((item:IExtraCartItem) => {return item.rateId == extraId});
  }

  getPriceWithExtras() {
    let price:number = parseInt(this.item.totalPriceForStayWithTaxes);
    this.extras.forEach((extra:IExtraCartItem) => {
      price += extra.totalPriceForStayWithTaxes * extra.quantity;
    });
    return price;
  }
  getSearchParams(): ISearchParams {
    return this.searchParams;
  }

}
